import { Box, CircularProgress, Grid, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Formtitle, IperiaLayout } from '../../components'
import ParentExtractedDataEdit from '../../components/iperia/ParentExtractedDataEdit'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { IperiaFormDefaultState } from '../../utils/constants'
import type { IperiaFormType } from '../../utils/sharedTypes'

type ParentAddressFields = 'parent_address' | 'parent_address_line_2' | 'parent_city' | 'parent_region' | 'parent_postal_code' | 'parent_country'

const ParentEmployeurAddress = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, updateParentEmployeur } = useIperiaStatus()

  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)
  const [errors, setErrors] = useState<Partial<Record<ParentAddressFields, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [editingExtractedData, setEditingExtractedData] = useState(
    () => !status?.parent_employeur?.parent_address && !status?.parent_employeur?.parent_city && !status?.parent_employeur?.parent_postal_code && !status?.parent_employeur?.parent_country,
  )

  const steps = [
    { label: 'Informations', url: 'parent-employeur' },
    { label: 'Adresse', url: 'parent-employeur/adresse' },
  ]

  useEffect(() => {
    if (!status?.parent_employeur) return

    setForm({
      ...IperiaFormDefaultState,
      data: {
        ...IperiaFormDefaultState.data,
        parent_title: status.parent_employeur.parent_title || '',
        parent_first_name: status.parent_employeur.parent_first_name || '',
        parent_last_name: status.parent_employeur.parent_last_name || '',
        parent_email: status.parent_employeur.parent_email || '',
        parent_mobile_phone: status.parent_employeur.parent_mobile_phone || '',
        parent_pajemploi_number: status.parent_employeur.parent_pajemploi_number || '',
        parent_address: status.parent_employeur.parent_address || '',
        parent_address_line_2: status.parent_employeur.parent_address_line_2 || '',
        parent_city: status.parent_employeur.parent_city || '',
        parent_region: status.parent_employeur.parent_region || '',
        parent_postal_code: status.parent_employeur.parent_postal_code || '',
        parent_country: status.parent_employeur.parent_country || 'France',
      },
      steps,
    })
  }, [status?.parent_employeur])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  // Seuls les champs requis comptent : une erreur affichée puis corrigée ne doit pas bloquer l'envoi.
  const isFormValid = Boolean(form.data.parent_address)
    && Boolean(form.data.parent_city)
    && Boolean(form.data.parent_postal_code)
    && Boolean(form.data.parent_country)

  const handleChange = (field: ParentAddressFields, value: string) => {
    setForm((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async () => {
    if (!isFormValid) {
      // Même logique que l'écran précédent : on dit ce qui manque plutôt que de griser le bouton.
      setErrors((prev) => ({
        ...prev,
        ...(!form.data.parent_address ? { parent_address: "L'adresse est requise" } : {}),
        ...(!form.data.parent_postal_code ? { parent_postal_code: 'Le code postal est requis' } : {}),
        ...(!form.data.parent_city ? { parent_city: 'La ville est requise' } : {}),
        ...(!form.data.parent_country ? { parent_country: 'Le pays est requis' } : {}),
      }))
      window.setTimeout(() => {
        document.querySelector('[aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 0)
      return
    }
    if (!globalId) {
      setSubmitError('Identifiant du dossier manquant.')
      return
    }
    setSubmitting(true)
    setSubmitError(null)

    try {
      const parentData = {
        parent_title: form.data.parent_title,
        parent_first_name: form.data.parent_first_name,
        parent_last_name: form.data.parent_last_name,
        parent_email: form.data.parent_email,
        parent_mobile_phone: form.data.parent_mobile_phone,
        parent_pajemploi_number: form.data.parent_pajemploi_number,
        parent_address: form.data.parent_address,
        parent_address_line_2: form.data.parent_address_line_2,
        parent_city: form.data.parent_city,
        parent_region: form.data.parent_region,
        parent_postal_code: form.data.parent_postal_code,
        parent_country: form.data.parent_country,
      }

      await updateParentEmployeur(parentData)
      navigate(`/iperia/${globalId}/signature`)
    } catch (err) {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (!globalId) return
    navigate(`/iperia/${globalId}/parent-employeur`)
  }

  return (
    <IperiaLayout 
      productName={form.data.product_name} 
      handleBack={handleBack} 
      handleNext={handleSubmit} 
      submitting={submitting} 
      submittingNext={submitting}
      alreadyConfirmed={status.can_sign_bi || status.workflow_status === 'bi_blocked_late_start' || !!status.signature_blocked_message}
    >
      <Formtitle
        title="Adresse du parent employeur"
        subtitle="Vérifiez que cette adresse correspond au bulletin de paie déposé."
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Adresse"
            inputProps={{ autoComplete: 'off' }}
            value={form.data.parent_address || ''}
            onChange={(e) => handleChange('parent_address', e.target.value)}
            error={Boolean(errors.parent_address)}
            helperText={errors.parent_address}
            disabled={submitting || !editingExtractedData}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Complément d'adresse"
            inputProps={{ autoComplete: 'off' }}
            value={form.data.parent_address_line_2 || ''}
            onChange={(e) => handleChange('parent_address_line_2', e.target.value)}
            disabled={submitting || !editingExtractedData}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Code postal"
            value={form.data.parent_postal_code || ''}
            onChange={(e) => handleChange('parent_postal_code', e.target.value)}
            error={Boolean(errors.parent_postal_code)}
            helperText={errors.parent_postal_code}
            disabled={submitting || !editingExtractedData}
            inputProps={{ maxLength: 5, inputMode: 'numeric', autoComplete: 'off' }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Ville"
            inputProps={{ autoComplete: 'off' }}
            value={form.data.parent_city || ''}
            onChange={(e) => handleChange('parent_city', e.target.value)}
            error={Boolean(errors.parent_city)}
            helperText={errors.parent_city}
            disabled={submitting || !editingExtractedData}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Région"
            inputProps={{ autoComplete: 'off' }}
            value={form.data.parent_region || ''}
            onChange={(e) => handleChange('parent_region', e.target.value)}
            disabled={submitting || !editingExtractedData}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Pays"
            inputProps={{ autoComplete: 'off' }}
            value={form.data.parent_country || ''}
            onChange={(e) => handleChange('parent_country', e.target.value)}
            error={Boolean(errors.parent_country)}
            helperText={errors.parent_country}
            disabled={submitting || !editingExtractedData}
          />
        </Grid>
      </Grid>

      {globalId && !editingExtractedData && (
        <Box sx={{ mb: 3 }}>
          <ParentExtractedDataEdit
            onEnabled={() => setEditingExtractedData(true)}
          />
        </Box>
      )}

      {submitError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {submitError}
        </Typography>
      )}
    </IperiaLayout>
  )
}

export default ParentEmployeurAddress
